/* eslint-disable import/prefer-default-export */
import includes from 'lodash/includes';
import isDomUsable from '@fenderdigital/dom';
import { track } from '@fenderdigital/analytics';
import ttiPolyfill from 'tti-polyfill';
import segment from '../core/segment';
import Cookie from './cookie';
import { getUTMData } from './payments';

function playerEventData(chapter, lesson, course, playlist, path) {
  const meta = {
    chapter: null,
    lesson: null,
    lessonType: null,
    course: null,
    playlist: null,
    path: null,
  };
  if (chapter) {
    meta.chapter = chapter.title || null;
  }
  if (lesson) {
    meta.lesson = lesson.title || null;
    meta.lessonType = lesson.type || null;
  }
  if (course) {
    meta.course = course.title || null;
  }
  if (playlist) {
    meta.playlist = playlist.title || null;
  }
  if (path) {
    meta.path = path.title || null;
  }

  return meta;
}

export function getTrackEvent(dataObj, fcId = null, path = null) {
  const type = dataObj.type || dataObj.lessonType;
  const title = dataObj.title || dataObj.overlayTitle;
  const properties = {
    course: null,
    lesson: null,
    type,
    path,
    level: dataObj.level || null,
    fcId,
  };
  let trackEventType = 'Lesson Click';
  if (type === 'song' || type === 'skill' || type === 'riff') {
    trackEventType = 'Course Click';
    properties.course = title;
  } else if (type === 'artist' || type === 'circle') {
    trackEventType = 'Browse Page Click';
    properties.page = title;
  } else {
    properties.lesson = title;
  }
  return {
    eventType: trackEventType,
    properties,
  };
}

export function getPracticeTrackEvent(eventType, lessonObj, path = '') {
  const { id, title } = lessonObj || {};
  return {
    eventType,
    properties: {
      practice_sheet_id: id,
      practice_sheet_title: title,
      referrer: path,
    },
  };
}

export function getSearchTrackEvent(hit, query = '', index = 0) {
  if (!hit) return null;
  const { objectType, slug, type } = hit;
  return {
    eventType: 'Completed Search',
    properties: {
      lesson_type: type,
      objectType,
      search_location: isDomUsable() ? window.location.pathname : '',
      search_text: query,
      selection_location: index,
      slug,
    },
  };
}

function getFCTracking(fenderConnect, signedUp = false) {
  if (!fenderConnect) return {};
  const { userID } = fenderConnect;
  return {
    cart_id: userID,
    fcId: userID,
    order_id: userID,
    signedUp,
  };
}

function getPlanTracking(plan) {
  if (!plan) return {};
  const { name, pricing } = plan;
  const { currency, price } = pricing || {};
  const sku = plan ? plan['product-rate-plan-sku'] : '0001';
  const parsedPrice = price ? parseFloat(price) : 0.0;
  return {
    currency,
    revenue: parsedPrice,
    sku,
    total: parsedPrice,
    products: [
      {
        quantity: 1,
        name: `Fender Play ${name}`,
        sku,
        id: sku,
        product_id: sku,
        price: parsedPrice,
      },
    ],
  };
}

function getSessionTracking() {
  const utm = getUTMData();
  return {
    affiliation: Cookie.getAffiliateId(),
    clickRef: Cookie.getClickRef(),
    country_code: Cookie.getCountry(),
    campaignId: Cookie.getCampaignId(),
    templateId: Cookie.getTemplateId(),
    ...utm,
  };
}

function getPaymentTracking(payment) {
  if (!payment) return {};
  const { paymentError, paymentMethod, redemptionCode } = payment;
  return {
    payment_method: paymentMethod,
    payment_error: paymentError,
    promo_code: redemptionCode,
  };
}

function getLocationTracking(type) {
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
  return type === 'Completed Order' ? { time_zone_identifier: timeZone, locale } : {};
}

// This is meant to replace onboarding-utils trackOrder and getTrackProperties functions
export function trackOrder(type = 'Track Order', fenderConnect, selectedPlan, payment) {
  const { signedUp } = payment || {};
  const trackProperties = {
    ...getFCTracking(fenderConnect, signedUp),
    ...getPaymentTracking(payment),
    ...getPlanTracking(selectedPlan),
    ...getSessionTracking(),
    ...getLocationTracking(type),
  };
  return segment.event(type, trackProperties);
}

export function triggerPlayerWatchingEvent(
  percentage,
  chapter,
  lesson,
  course = null,
  playlist = null,
  path = null,
) {
  if (!includes([0, 25, 50, 75, 100], percentage)) {
    return;
  }
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
  const meta = playerEventData(chapter, lesson, course, playlist, path);
  const properties = {
    ...meta,
    watchedTo: percentage,
    time_zone_identifier: timeZone,
    locale,
  };
  segment.event('Watched Video', properties);
}

export function triggerWatchedVideoEvent(properties) {
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
  properties.locale = locale;
  properties.timeZone = timeZone;
  segment.event('Watched Video', properties);
}

export function trackNewOnPlay(properties) {
  const siteId = process.env.CLIENT_SITE_ID;
  const { timeZone, locale } = Intl.DateTimeFormat().resolvedOptions();
  const { sessionId, userID } = properties || {};
  track('New on Play', {
    locale,
    timezone: timeZone,
    fc_id: userID,
    fc_session_id: sessionId,
    site_id: siteId,
  });
}

function onGAReady(ga) {
  ttiPolyfill.getFirstConsistentlyInteractive().then(tti => {
    ga('send', {
      hitType: 'timing',
      timingCategory: 'Performance Metrics',
      timingVar: 'TTI',
      timingValue: tti,
    });
  });

  // set in index.html
  // eslint-disable-next-line no-underscore-dangle
  if (Array.isArray(window._fender_perf)) {
    // eslint-disable-next-line no-underscore-dangle
    window._fender_perf.forEach(([metricName, time]) => {
      ga('send', {
        hitType: 'timing',
        timingCategory: 'Performance Metrics',
        timingVar: metricName,
        timingValue: time,
      });
    });
  }
}

export function trackPerformanceMetrics() {
  let attempts = 0;
  // using this instead of segment.ready as anything blocked by adblock will
  // cause ready to not fire. twitter ads for example
  const gaReadyInterval = setInterval(() => {
    if (window.ga || attempts > 100) {
      clearInterval(gaReadyInterval);
    }

    if (window.ga) {
      onGAReady(window.ga);
    }
    attempts++;
  }, 250);
}
